import UserEditor, { DEFAULT_USER_DATA } from 'components/UserEditor/UserEditor';
import UserEditSubmitButton from 'components/UserEditSubmitButton/UserEditSubmitButton';
import { PermsContext } from 'contexts/PermsContext';
import { useContext, useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { FaCopy } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { createFusionUser } from 'services/user/user.service';
import { notifyErrorToast, notifySuccessToast } from 'utils/notifications/toast.utils';

export default function CreateUser() {
  const [formData, setFormData] = useState(DEFAULT_USER_DATA);
  const [userError, setUserError] = useState('');
  const [copied, setCopied] = useState(true);
  const [activateUrl, _setActivateUrl] = useState('');
  const setActivateUrl = (newValue) => {
    setCopied(false);
    _setActivateUrl(newValue);
  };
  const [errorMsg, setErrorMsg] = useState('');
  const [canAssignSM, setCanAssignSM] = useState(false);
  const [canAssignDM, setCanAssignDM] = useState(false);
  const perms = useContext(PermsContext);

  const onSubmit = async () => {
    if (!submitButtonEnabled()) return;

    setErrorMsg('');

    try {
      const res = await createFusionUser(
        formData.roles,
        formData.base,
        formData.department,
        formData.rank,
        formData.name,
        formData.email,
        formData.employeeId,
        formData.phone,
        formData.gender,
        formData.birthday,
        JSON.stringify({
          street: formData.address,
          city: formData.city,
          state: formData.state,
          zipcode: formData.zipcode,
        }),
        JSON.stringify({
          name: formData.eContactName,
          phone: formData.eContactPhone,
        }),
        formData.isAdmin || false,
        formData.maintenance !== 0,
        formData.maintenance,
        Number(formData.serviceManager) || 0,
        Number(formData.deploymentManager) || 0,
      );

      if (res.isError) {
        if (res?.message) {
          toast.error(res.message);
        }
      }
      if (res.status === 201) {
        setActivateUrl(res.data.Link);
        notifySuccessToast('User', { isAddOperation: true });
      }
    } catch (error) {
      notifyErrorToast('User', { isAddOperation: true });
    }
  };

  //Set permission to assign service manager role, only on mount
  useEffect(() => {
    const accessLevel = perms?.accessLevel;
    const serviceManagerFlag = perms?.serviceManagerFlag;
    setCanAssignSM(
      accessLevel === 13 ||
        accessLevel === 8 ||
        ((accessLevel === 11 || accessLevel === 12) && serviceManagerFlag),
    );
    // Deployment manager grant available to access levels 8, 11, 13.
    setCanAssignDM(accessLevel === 8 || accessLevel === 11 || accessLevel === 13);
  }, [perms?.accessLevel, perms?.serviceManagerFlag]);

  //reset the form data on discard
  const onDiscard = () => setFormData(DEFAULT_USER_DATA);

  const submitButtonEnabled = () => {
    //check required values
    return (
      formData.name &&
      formData.email &&
      formData.roles &&
      formData.base &&
      formData.department &&
      formData.rank &&
      !userError
    );
  };

  return (
    <div
      className="user-select-none w-100 h-100 d-flex flex-column overflow-auto flex-grow-1"
      onClick={() => {
        if (activateUrl) setActivateUrl('');
      }}
    >
      <UserEditor value={formData} onChange={setFormData} onUserError={setUserError} />
      <div className="d-flex flex-column flex-lg-row align-items-lg-baseline justify-content-lg-end mt-lg-2 gap-2">
        {errorMsg && <p data-error="true">{errorMsg}</p>}
        <div className="d-flex flex-column-reverse flex-lg-row-reverse align-items-lg-center gap-2 justify-content-center align-content-center">
          <UserEditSubmitButton onSubmit={onSubmit} disabled={!submitButtonEnabled()} />
          {/* Cancel Button */}
          <Button variant="dark" className="p-3 h-100 border border-secondary" onClick={onDiscard}>
            Cancel
          </Button>
          {/* Permission checkboxes — stacked vertically */}
          <div className="d-flex flex-column gap-1 align-items-start">
            <Form.Check
              type="checkbox"
              checked={Boolean(formData.isAdmin)}
              onChange={(e) => setFormData({ ...formData, isAdmin: e.target.checked })}
              label="User is an Admin"
            />
            {canAssignSM && (
              <Form.Check
                type="checkbox"
                checked={Boolean(formData.serviceManager)}
                onChange={(e) =>
                  setFormData({ ...formData, serviceManager: e.target.checked ? 1 : 0 })
                }
                label="User is a Service Manager"
              />
            )}
            {canAssignDM && (
              <Form.Check
                type="checkbox"
                checked={Boolean(formData.deploymentManager)}
                onChange={(e) =>
                  setFormData({ ...formData, deploymentManager: e.target.checked ? 1 : 0 })
                }
                label="User is a Deployment Manager"
              />
            )}
          </div>
        </div>
      </div>

      {/* Popup with link copy */}
      {activateUrl && (
        <div
          className="position-absolute top-50 start-50 z-2 bg-primary-shadow text-white d-flex flex-column gap-2 rounded-3 p-2 border border-1 border-black text-center translate-middle w-100 w-sm-md"
          onClick={(e) => e.stopPropagation()}
        >
          <p className="mb-0 fs-6">This link will be sent to the email provided for this user.</p>
          <p className="mb-0 fs-6">
            Here is a copy for you in case that email fails to be received.
          </p>
          <div className="bg-background text-white user-select-text d-flex rounded-5 align-items-center justify-content-between border border-1 border-black">
            {activateUrl}
            <Button
              className="btn-transparent"
              onClick={() => {
                navigator.clipboard.writeText(activateUrl);
                setCopied(true);
              }}
            >
              <FaCopy />
            </Button>
          </div>
          {copied && <p className="text-success">URL copied to clipboard!</p>}
        </div>
      )}
    </div>
  );
}
